import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllAccounts, addJournalVoucher, getAllJournalVouchers, deleteJournalVoucher, updateJournalVoucher } from "../firebase/database";
import { theme } from "../utils/theme";

const C = theme.colors;

export default function JournalVoucher() {
  const [jvs, setJvs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    narration: "",
    entries: [
      { accountId: "", accountName: "", dr: 0, cr: 0 },
      { accountId: "", accountName: "", dr: 0, cr: 0 }
    ]
  });

  useEffect(() => {
    loadData();
  }, []);

  const handleAddEntry = () => {
    setFormData(prev => ({
      ...prev,
      entries: [...prev.entries, { accountId: "", accountName: "", dr: 0, cr: 0 }]
    }));
  };

  const handleRemoveEntry = (index) => {
    if (formData.entries.length <= 2) return;
    setFormData(prev => ({
      ...prev,
      entries: prev.entries.filter((_, i) => i !== index)
    }));
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...formData.entries];
    if (field === "accountId") {
      const account = accounts.find(a => a.id === value);
      newEntries[index].accountId = value;
      newEntries[index].accountName = account ? account.name : "";
    } else {
      newEntries[index][field] = parseFloat(value) || 0;
    }
    setFormData(prev => ({ ...prev, entries: newEntries }));
  };

  const totalDr = formData.entries.reduce((sum, e) => sum + e.dr, 0);
  const totalCr = formData.entries.reduce((sum, e) => sum + e.cr, 0);
  const isBalanced = totalDr === totalCr && totalDr > 0;

  const loadData = async () => {
    try {
      setLoading(true);
      const [allJvs, allAccounts] = await Promise.all([
        getAllJournalVouchers(),
        getAllAccounts()
      ]);
      setJvs(allJvs);
      setAccounts(allAccounts);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (jv) => {
    setFormData({
      date: jv.date,
      narration: jv.narration,
      entries: jv.entries.map(e => ({ ...e }))
    });
    setEditingId(jv.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      narration: "",
      entries: [
        { accountId: "", accountName: "", dr: 0, cr: 0 },
        { accountId: "", accountName: "", dr: 0, cr: 0 }
      ]
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isBalanced) {
      alert("Total Debit must equal Total Credit and be greater than zero.");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateJournalVoucher(editingId, {
          ...formData,
          totalAmount: totalDr
        });
      } else {
        await addJournalVoucher({
          ...formData,
          totalAmount: totalDr
        });
      }
      resetForm();
      loadData();
    } catch (err) {
      console.error("Error saving JV:", err);
      alert("Failed to save Journal Voucher");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, jvNo) => {
    if (!window.confirm(`Delete Journal Voucher ${jvNo}?`)) return;
    try {
      await deleteJournalVoucher(id);
      setJvs(jvs.filter(j => j.id !== id));
    } catch (err) {
      console.error("Error deleting JV:", err);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.muted }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: 24, fontFamily: "DM Sans, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Link to="/admin" style={{ color: C.muted }}><svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg></Link>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Journal Vouchers</h1>
          </div>
          <p style={{ color: C.muted, margin: "4px 0 0 32px" }}>Manual Debit/Credit adjustments between accounts</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{ padding: "10px 20px", background: C.accent, color: "white", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}
        >
          + New Entry
        </button>
      </div>

      {/* JV List */}
      <div style={{ background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.dim, borderBottom: `1px solid ${C.border}` }}>
              <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Date / JV No</th>
              <th style={{ padding: 12, textAlign: "left", fontSize: 13, color: C.muted }}>Narration</th>
              <th style={{ padding: 12, textAlign: "right", fontSize: 13, color: C.muted }}>Amount</th>
              <th style={{ padding: 12, textAlign: "center", fontSize: 13, color: C.muted }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jvs.map(jv => (
              <tr key={jv.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: 12 }}>
                  <div style={{ fontWeight: 600, color: C.text }}>{jv.jvNo}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{jv.date}</div>
                </td>
                <td style={{ padding: 12, color: C.text, fontSize: 14 }}>{jv.narration}</td>
                <td style={{ padding: 12, textAlign: "right", fontWeight: 600, color: C.text }}>₹{jv.totalAmount?.toLocaleString()}</td>
                <td style={{ padding: 12, textAlign: "center" }}>
                  <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                    <button onClick={() => handleEdit(jv)} style={{ padding: "4px 8px", background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}>Edit</button>
                    <button onClick={() => handleDelete(jv.id, jv.jvNo)} style={{ padding: "4px 8px", background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 4, fontSize: 12, cursor: "pointer" }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
            {jvs.length === 0 && (
              <tr><td colSpan="4" style={{ padding: 40, textAlign: "center", color: C.muted }}>No journal vouchers found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "white", borderRadius: 16, width: "100%", maxWidth: 800, maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
            <h2 style={{ margin: "0 0 20px 0", fontSize: 20 }}>New Journal Voucher</h2>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 24 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Date</label>
                <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={{ width: "100%", padding: 10, border: "1px solid #ddd", borderRadius: 8 }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Narration</label>
                <input type="text" value={formData.narration} onChange={e => setFormData({...formData, narration: e.target.value})} placeholder="Description of the adjustment" style={{ width: "100%", padding: 10, border: "1px solid #ddd", borderRadius: 8 }} />
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 20 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #eee" }}>
                  <th style={{ textAlign: "left", padding: 8, fontSize: 12 }}>Account</th>
                  <th style={{ textAlign: "right", padding: 8, fontSize: 12, width: 120 }}>Debit (Dr)</th>
                  <th style={{ textAlign: "right", padding: 8, fontSize: 12, width: 120 }}>Credit (Cr)</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {formData.entries.map((entry, index) => (
                  <tr key={index} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "8px 0" }}>
                      <select 
                        value={entry.accountId} 
                        onChange={e => handleEntryChange(index, "accountId", e.target.value)}
                        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6 }}
                      >
                        <option value="">Select Account</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.nature})</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "8px 0" }}>
                      <input 
                        type="number" 
                        value={entry.dr || ""} 
                        onChange={e => handleEntryChange(index, "dr", e.target.value)}
                        placeholder="0.00"
                        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6, textAlign: "right" }}
                      />
                    </td>
                    <td style={{ padding: "8px 0" }}>
                      <input 
                        type="number" 
                        value={entry.cr || ""} 
                        onChange={e => handleEntryChange(index, "cr", e.target.value)}
                        placeholder="0.00"
                        style={{ width: "100%", padding: 8, border: "1px solid #ddd", borderRadius: 6, textAlign: "right" }}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button onClick={() => handleRemoveEntry(index)} style={{ border: "none", background: "none", color: "#EF4444", cursor: "pointer", fontSize: 18 }}>×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: "12px 0" }}>
                    <button onClick={handleAddEntry} style={{ background: "none", border: "1px dashed #3B82F6", color: "#3B82F6", padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>+ Add Line</button>
                  </td>
                  <td style={{ textAlign: "right", padding: 12, fontWeight: 700, color: totalDr === totalCr ? "#059669" : "#DC2626" }}>{totalDr.toLocaleString()}</td>
                  <td style={{ textAlign: "right", padding: 12, fontWeight: 700, color: totalDr === totalCr ? "#059669" : "#DC2626" }}>{totalCr.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24, borderTop: "1px solid #eee", paddingTop: 20 }}>
              <div style={{ fontSize: 13, color: totalDr === totalCr ? "#059669" : "#DC2626", fontWeight: 600 }}>
                {totalDr === totalCr ? "✓ Entry is balanced" : "⚠ Debits and Credits must be equal"}
              </div>
              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={resetForm} style={{ padding: "10px 20px", background: "white", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer" }}>Cancel</button>
                <button 
                  onClick={handleSubmit} 
                  disabled={saving || !isBalanced}
                  style={{ padding: "10px 24px", background: isBalanced ? C.accent : "#9CA3AF", color: "white", border: "none", borderRadius: 8, fontWeight: 600, cursor: isBalanced ? "pointer" : "not-allowed" }}
                >
                  {saving ? "Saving..." : (editingId ? "Update Journal Voucher" : "Save Journal Voucher")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
